import * as ko from 'knockout';
import * as i18nextko from 'i18next-ko';
import type { ReplyCardViewModel } from '../../../reply-card/ko/replyCardViewModel';
import type { CardsWallRuntimeModel } from './cardsWallExchangeModel';
import { format, type Locale } from 'date-fns'; // TODO(date-fns)
import { Italian } from 'flatpickr/dist/l10n/it';
import { featuredCardLayout } from '../../constants';
import type { CardsWallEndpointType, QueryInput } from '../../cardsWallUtils';
import { localeSwitcher } from '../../../../translations/localeSwitcher';
import type { SelectTypeOption } from '../../../../utils/types';

export class CardsWallRuntimeViewModel {

    public readonly cards: ko.ObservableArray<ReplyCardViewModel>;
    public readonly paginationEnabled: ko.Observable<boolean>;
    public readonly filtersEnabled: ko.Observable<boolean>;
    public readonly preloadedItems: ko.Observable<number>;
    public readonly precomputedItemsTotal: ko.Observable<number>;
    public readonly shortLayout: ko.Observable<boolean>;
    public readonly darkTheme: ko.Observable<boolean>;

    public readonly hidePager: ko.PureComputed<boolean>;
    public readonly hideEmptyResult: ko.PureComputed<boolean>;
    // public readonly hideFilters: ko.PureComputed<boolean>;

    public readonly categoriesSelectValue: ko.Observable<string>;
    private readonly locale: ko.Observable<Locale>;
    public readonly tempDatePickerDate: ko.Observable<Date>;
    public readonly datePickerDate: ko.Observable<Date>;
    public readonly datePickerValue: ko.Computed<string>;
    public readonly searchInputValue: ko.Observable<string>;
    public datePickerConfig: { [key: string]: any };
    public readonly clearAllNewsroomFilters: ko.PureComputed<boolean>;

    // Insights filter
    public readonly tempCategories: ko.ObservableArray<SelectTypeOption>;
    public readonly categories: ko.ObservableArray<SelectTypeOption>;
    public readonly categoriesOptions: ko.ObservableArray<SelectTypeOption>;
    public readonly categoriesLabel: ko.Computed<string>;
    public readonly tempIndustries: ko.ObservableArray<SelectTypeOption>;
    public readonly industries: ko.ObservableArray<SelectTypeOption>;
    public readonly industriesOptions: ko.ObservableArray<SelectTypeOption>;
    public readonly industriesLabel: ko.Computed<string>;
    public readonly tempServices: ko.ObservableArray<SelectTypeOption>;
    public readonly services: ko.ObservableArray<SelectTypeOption>;
    public readonly servicesOptions: ko.ObservableArray<SelectTypeOption>;
    public readonly servicesLabel: ko.Computed<string>;
    public readonly clearAllInsightsFilters: ko.PureComputed<boolean>;

    // Business filter
    public readonly tempImpacts: ko.ObservableArray<SelectTypeOption>;
    public readonly impacts: ko.ObservableArray<SelectTypeOption>;
    public readonly impactsOptions: ko.ObservableArray<SelectTypeOption>;
    public readonly impactsLabel: ko.Computed<string>;
    public readonly clearAllImpactsFilters: ko.PureComputed<boolean>;

    public readonly queryInput: ko.Observable<QueryInput>;

    public readonly endpointType: ko.Observable<CardsWallEndpointType>;

    constructor(model: CardsWallRuntimeModel) {
        this.locale = ko.observable<Locale>(localeSwitcher(model.siteLanguage));
        this.cards = ko.observableArray<ReplyCardViewModel>([]);
        this.paginationEnabled = ko.observable<boolean>(model.model.paginationEnabled);
        this.filtersEnabled = ko.observable<boolean>(model.model.filtersEnabled);
        this.shortLayout = ko.observable<boolean>(model.model.shortLayout);
        this.darkTheme = ko.observable<boolean>(model.model.darkTheme);
        this.preloadedItems = ko.observable<number>(model.preloadedItems);
        this.precomputedItemsTotal = ko.observable<number>(model.precomputedItemsTotal);
        this.hidePager = ko.pureComputed<boolean>((): boolean =>
            (this.cards().length === 0 && this.preloadedItems() === this.precomputedItemsTotal()) || !this.paginationEnabled());
        this.hideEmptyResult = ko.pureComputed<boolean>((): boolean =>
            this.cards().length + this.preloadedItems() > 0);
        if (model.queryInput?.sections?.length === 1) {
            this.categoriesSelectValue = ko.observable<string>(model.queryInput.sections[0]);
        } else {
            this.categoriesSelectValue = ko.observable<string>();
        }
        this.tempDatePickerDate = ko.observable<Date>();
        this.datePickerDate = ko.observable<Date>();
        if (model.queryInput?.startDate) {
            this.tempDatePickerDate(new Date(model.queryInput.startDate));
            this.datePickerDate(new Date(model.queryInput.startDate));
        }
        this.datePickerValue = ko.computed<string>(() => {
            const pickerDate = this.datePickerDate();
            if (pickerDate) {
                return format(pickerDate, 'MMMM yyyy', { locale: this.locale() });
            } else {
                return 'Year and month';
            }
        });
        if (model.queryInput?.searchInput) {
            this.searchInputValue = ko.observable<string>(model.queryInput.searchInput);
        } else {
            this.searchInputValue = ko.observable<string>();
        }
        this.clearAllNewsroomFilters = ko.pureComputed<boolean>({
            read: () => {
                return !!this.datePickerDate() || !!this.searchInputValue();
            },
            write: () => {
                if (this.datePickerDate()) {
                    this.datePickerDate(undefined);
                }
                if (this.searchInputValue()) {
                    this.searchInputValue(undefined);
                }
            },
            owner: this
        });

        this.tempCategories = ko.observableArray<SelectTypeOption>([]);
        this.categories = ko.observableArray<SelectTypeOption>([]);
        this.categoriesOptions = ko.observableArray<SelectTypeOption>([]);
        this.categoriesLabel = ko.computed<string>(
            () => getFilterLabel(this.categories(), 'Content type')
        );
        this.tempIndustries = ko.observableArray<SelectTypeOption>([]);
        this.industries = ko.observableArray<SelectTypeOption>([]);
        this.industriesOptions = ko.observableArray<SelectTypeOption>([]);
        this.industriesLabel = ko.computed<string>(
            () => getFilterLabel(this.industries(), 'Industry')
        );
        this.tempServices = ko.observableArray<SelectTypeOption>([]);
        this.services = ko.observableArray<SelectTypeOption>([]);
        this.servicesOptions = ko.observableArray<SelectTypeOption>([]);
        this.servicesLabel = ko.computed<string>(
            () => getFilterLabel(this.services(), 'Service')
        );
        this.clearAllInsightsFilters = ko.pureComputed<boolean>({
            read: () => {
                return this.categories().length > 0 
                || this.industries().length > 0 
                || this.services().length > 0 
                || !!this.searchInputValue();
            },
            write: () => {
                if (this.categories().length) {
                    this.categories([]);
                }
                if (this.industries().length) {
                    this.industries([]);
                }
                if (this.services().length) {
                    this.services([]);
                }
                if (this.searchInputValue()) {
                    this.searchInputValue(undefined);
                }
            },
            owner: this
        });

        this.tempImpacts = ko.observableArray<SelectTypeOption>([]);
        this.impacts = ko.observableArray<SelectTypeOption>([]);
        this.impactsOptions = ko.observableArray<SelectTypeOption>([]);
        this.impactsLabel = ko.pureComputed<string>(
            () => getFilterLabel(this.impacts(), 'Business impact')
        );
        this.clearAllImpactsFilters = ko.pureComputed<boolean>({
            read: () => {
                return this.impacts().length > 0 
                || this.industries().length > 0
                || !!this.searchInputValue();
            },
            write: () => {
                if (this.impacts().length) {
                    this.impacts([]);
                }
                if (this.industries().length) {
                    this.industries([]);
                }
                if (this.searchInputValue()) {
                    this.searchInputValue(undefined);
                }
            },
            owner: this
        });

        this.queryInput = ko.observable<QueryInput>({ ...model.queryInput });

        this.endpointType = ko.observable<CardsWallEndpointType>(model.model.endpointType);
    }

    public withDatePickerConfig(): CardsWallRuntimeViewModel {
        const onChange = (dates: Date[]) => this.tempDatePickerDate(dates?.[0]);
        const config: { [key: string]: any } = {
            options: {
                appendTo: '[data-picker-toggle]',
                inline: true,
                plugins: [
                    { hook: 'monthSelectPlugin', options: { shorthand: true } }
                ],
                defaultDate: this.datePickerDate(),
                onChange
            }
        };
        // TODO Handle different locales
        if (this.locale().code === 'it') {
            config.options.locale = Italian;
        }
        this.datePickerConfig = config;

        return this;
    }

    public isFeaturedCard(card: ReplyCardViewModel): boolean {
        return card?.layout() === featuredCardLayout;
    }

    public t(key: string): ko.Computed<string> {
        return i18nextko.t(key);
    }
}

function getFilterLabel(filterArray: SelectTypeOption[], typeLabel: string): string {
    return filterArray.sort(optionsSort).map(o => o.label).join(', ') || typeLabel;
}

export function optionsSort(a: SelectTypeOption, b: SelectTypeOption) {
    return a.label.localeCompare(b.label);
}
